import React from "react";
import { useSearchParams } from "react-router";

import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import { parseCityKey, cityKey as toCityKey } from "@/types/area";

export type AreasTab = "list" | "map";

/**
 * The page's tabs. `issues` was a third until 2026-11-01 (now the Area tab of
 * `/tina/audits`); a persisted or linked `issues` is narrowed away to `list` below.
 */
export const AREAS_TABS: readonly AreasTab[] = ["list", "map"];

export const isAreasTab = (value: string | null): value is AreasTab =>
  AREAS_TABS.some((tab) => tab === value);

/**
 * The page state every `/areas` tab shares: the active tab and the picked city.
 *
 * The tab is persisted like the audits page's, and an initial `?tab=` wins once. The city lives
 * only in the URL (`?city=London&countryCode=GB`), so a tab switch keeps it and a link carries it.
 */
export const useAreaPageState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const [stored, setTab] = usePersistedState<AreasTab>("areas:tab", "list");

  const appliedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!isAreasTab(requested) || appliedRef.current === requested) return;
    appliedRef.current = requested;
    setTab(requested);
  }, [requested, setTab]);

  // Narrowed on read: the persisted value can be a tab an older build wrote.
  const tab: AreasTab = isAreasTab(stored) ? stored : "list";

  const city = searchParams.get("city");
  const countryCode = searchParams.get("countryCode");
  const cityKey =
    city && countryCode ? toCityKey({ city, countryCode }) : undefined;

  const setCityKey = React.useCallback(
    (key: string | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (key) {
            const parsed = parseCityKey(key);
            next.set("city", parsed.city);
            next.set("countryCode", parsed.countryCode);
          } else {
            next.delete("city");
            next.delete("countryCode");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { tab, setTab, cityKey, setCityKey };
};
